import { Injectable } from '@angular/core';
import { ScreenSize } from '../interfaces/screen-size.interface';
import { fromEvent, BehaviorSubject, Observable, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const MOBILE_DEVICE_WIDTH = 576;
const TABLET_DEVICE_WIDTH = 1024;

@Injectable({ providedIn: 'root' })
export class MobileCheckServiceService {
  private _mobileDevice = false;
  private _tabletDevice = false;

  private screenSize$ = new BehaviorSubject<ScreenSize>(this.check());

  private lg = window.matchMedia('(max-width: 1199px)');
  private md = window.matchMedia('(max-width: 991px)');
  private sm = window.matchMedia('(max-width: 767px)');
  private xs = window.matchMedia('(max-width: 575px)');

  constructor() {
    this.initResizeListener();
  }

  /**
   * Cross-browser MediaQueryList change observable.
   * `addEventListener` is the modern API (Chrome 45+, Firefox 55+, Safari 14+).
   * Older Safari / legacy browsers only support the deprecated `addListener` API,
   * so we fall back to it to avoid silent failures.
   */
  private mediaQueryChange$(mql: MediaQueryList): Observable<Event> {
    return new Observable<Event>((subscriber) => {
      const handler = (event: Event) => subscriber.next(event);
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
      }
      // Safari < 14 / legacy browsers
      (mql as MediaQueryList).addListener(handler);
      return () => (mql as MediaQueryList).removeListener(handler);
    });
  }

  private initResizeListener(): void {
    // Listen to both resize events and matchMedia change events.
    // matchMedia fires precisely on breakpoint transitions (including when DevTools
    // closes and the viewport snaps back), avoiding the stale innerWidth problem
    // that occurs when debounce fires before the browser finishes reflowing.
    const mediaChanges = merge(
      this.mediaQueryChange$(this.lg),
      this.mediaQueryChange$(this.md),
      this.mediaQueryChange$(this.sm),
      this.mediaQueryChange$(this.xs),
    );

    merge(
      fromEvent(window, 'resize').pipe(debounceTime(200)),
      mediaChanges,
    ).subscribe(() => {
      const state = this.check();
      this.screenSize$.next(state);
    });
  }

  check(): ScreenSize {
    const width = window.innerWidth;
    this._mobileDevice = width <= MOBILE_DEVICE_WIDTH;
    this._tabletDevice = !this._mobileDevice && width <= TABLET_DEVICE_WIDTH;

    return {
      width,
      isMobile: this._mobileDevice,
      isTablet: this._tabletDevice
    };
  }

  /** Observable for components to subscribe */
  public get screenSizeChanges$(): Observable<ScreenSize> {
    return this.screenSize$.asObservable();
  }

  /** Synchronous accessors */
  public get mobile(): boolean {
    return this._mobileDevice;
  }

  public get mobileDevice(): boolean {
    return this._mobileDevice;
  }

  public get tabletDevice(): boolean {
    return this._tabletDevice;
  }

  public get desktop(): boolean {
    return !this._mobileDevice && !this._tabletDevice;
  }

  public get isLgSize(): boolean {
    return this.lg.matches;
  }

  public get isMdSize(): boolean {
    return this.md.matches;
  }

  public get isSmSize(): boolean {
    return this.sm.matches;
  }

  public get isXsSize(): boolean {
    return this.xs.matches;
  }
}
